/**
 * 租户管理
 * 接口文档：.trae/documents/tenant-openapi.yaml
 */
import { httpRequest } from '@/utils/umiRequest';

/** 租户字典项（下拉用，仅含启用中的租户） */
export interface TenantDictItem {
  id: number;
  name: string;
}

/** 租户绑定域名（域名的增删通过更新接口的 domains 全量替换表达） */
export interface TenantDomain {
  id: number;
  /** 纯净域名（小写 host，不含 scheme 与 port） */
  domain: string;
}

/** 租户列表项 */
export interface TenantItem {
  id: number;
  name: string;
  /** 0-停用 1-启用 */
  status: 0 | 1;
  remark: string | null;
  created_by: number;
  domain_count: number;
  /** 该租户下站点用户数 */
  user_count: number;
  /** 该租户下在线工单数 */
  ticket_count: number;
  created_at: string;
  updated_at: string;
  /** 飞书webhook */
  feishu_webhook: string | null;
}

/** 租户详情（含域名列表） */
export type TenantDetail = TenantItem & { domains: TenantDomain[] };

/** 租户列表查询参数 */
export interface TenantListRequest {
  page?: number;
  /** 不传或传 0 = 返回全量（不分页） */
  per_page?: number;
  /** 名称模糊 */
  name?: string;
  /** 0-停用 1-启用；不传 = 全部 */
  status?: 0 | 1;
  /** 按域名模糊反查租户 */
  domain?: string;
}

/** 租户列表响应 */
export interface TenantListData {
  lists: TenantItem[];
  total: number;
  page: number;
  per_page: number;
}

/** 新增/编辑租户参数（domains 可传完整 URL，服务端自动归一化；更新时为全量替换） */
export interface TenantSaveRequest {
  name?: string;
  remark?: string;
  status?: 0 | 1;
  domains?: string[];
  feishu_webhook?: string | null;
}

/**
 * 获取租户列表（分页 + 筛选）
 */
export async function getTenantList(data: TenantListRequest) {
  return await httpRequest.post<TenantListData>('/tenant/list', data);
}

/**
 * 获取租户字典（下拉用，仅启用中的租户）
 * 由 models/tenantModel 统一调用与缓存，供角色「可见租户」白名单、账号管理「租户」字段复用
 */
export async function getTenantDictionary(options?: Record<string, any>) {
  return await httpRequest.get<TenantDictItem[]>('/tenant/dictionary', {}, options);
}

/**
 * 获取租户详情（含域名列表）
 */
export async function getTenantDetail(id: number) {
  return await httpRequest.get<TenantDetail>(`/tenant/${id}`);
}

/**
 * 新增租户（可同时绑定域名）
 */
export async function createTenant(data: TenantSaveRequest) {
  return await httpRequest.post<TenantItem>('/tenant', data);
}

/**
 * 修改租户（domains 为全量替换：新增缺的、删除多的）
 */
export async function updateTenant(id: number, data: TenantSaveRequest) {
  return await httpRequest.put<TenantDetail>(`/tenant/${id}`, data);
}

/**
 * 启用 / 停用租户（停用后名下域名立即失效，且不再出现在字典下拉）
 */
export async function changeTenantStatus(id: number, status: 0 | 1) {
  return await httpRequest.put<{ id: number; status: 0 | 1 }>(`/tenant/${id}/status`, { status });
}

/**
 * 删除租户（软删 + 清域名；被角色引用或名下有用户/工单时后端返回 409 拒绝）
 */
export async function deleteTenant(id: number) {
  return await httpRequest.delete<void>(`/tenant/${id}`);
}
